// Выгрузка расшифровки в субтитры (SRT, VTT), Markdown и JSON.
//
// Обращение 6f371de1 (02.10.2026): кроме текста и Word — субтитры к видео и
// данные для интеграций. Форматы непонятны большинству, поэтому в интерфейсе
// они спрятаны за стрелкой рядом с «Скачать .txt» (MoreFormatsMenu), а
// здесь — только сборка содержимого, без React и без сети.
//
// Вход — список реплик { speaker, start, end, text }, где время в секундах или
// null. Собирается из двух источников:
//   - фрагменты экрана результата (`segmentsFromFragments`): числа у диаризации
//     и стерео, строка «00:00:07» у плоской расшифровки;
//   - текст записи Истории (`segmentsFromText`) — разбор тот же, что у экрана
//     (`transcriptParse.js`), своего парсера здесь нет.
//
// 🔴 Правило конца реплики для субтитров (`subtitleCues`). Субтитру нужен
// отрезок, а у части расшифровок есть только начало («[00:00:07]: …»):
//   1. известный конец берётся как есть, если он позже начала;
//   2. иначе конец — начало следующей реплики, если оно позже;
//   3. иначе (последняя реплика, или следующая в ту же секунду) — начало плюс
//      оценка по длине текста: 15 знаков в секунду, не меньше 2 секунд.
// Конец, равный началу, — это округление до секунд («0:00:05 - 0:00:05»), его
// считаем неизвестным. В JSON оценки не попадают: там неизвестное — null.
import { formatTimecodeHMMSS } from '../FormatTime';
import { cleanSpeakerName } from '../components/protocol/speakerLabels';
import { escapeMarkdown, transcriptToMarkdown } from './transcriptMarkdown';
import {
  parseClock, parseTimecodeRange, parseTranscriptText, splitLeadingTimecode,
} from './transcriptParse';

export const EXPORT_FORMATS = [
  { key: 'srt', label: 'Субтитры к видео', ext: 'srt', mime: 'application/x-subrip;charset=utf-8', needsTimes: true },
  { key: 'vtt', label: 'Субтитры для сайта', ext: 'vtt', mime: 'text/vtt;charset=utf-8', needsTimes: true },
  { key: 'md', label: 'Текст с разметкой', ext: 'md', mime: 'text/markdown;charset=utf-8', needsTimes: false },
  { key: 'json', label: 'Для разработчиков и CRM', ext: 'json', mime: 'application/json;charset=utf-8', needsTimes: false },
];

export const NO_TIMES_HINT = 'У этой записи нет времени реплик';

const CHARS_PER_SECOND = 15;
const MIN_CUE_SECONDS = 2;

const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const round3 = (value) => (value === null ? null : Math.round(value * 1000) / 1000);

/** Фрагменты экрана результата → реплики. */
export function segmentsFromFragments(fragments) {
  return (fragments || []).map((f) => {
    const fromString = parseTimecodeRange(f.timecode);
    return {
      speaker: f.speaker || null,
      start: num(f.start) ?? fromString.start,
      end: num(f.end) ?? (f.endTimecode ? parseClock(f.endTimecode) : fromString.end),
      text: String(f.text || '').trim(),
    };
  });
}

/** Таймкод в начале реплики («Иван: [00:00:07] Привет» из .txt) — тоже время. */
function withLeadingTime(speaker, timecode, text) {
  const lead = timecode ? { timecode, text } : splitLeadingTimecode(text);
  const { start, end } = parseTimecodeRange(lead.timecode);
  return { speaker, start, end, text: String(lead.text || '').trim() };
}

/** Текст записи Истории → реплики. */
export function segmentsFromText(text) {
  if (!text || !String(text).trim()) return [];
  const fragments = parseTranscriptText(text);
  if (fragments) {
    return fragments.map((f) => withLeadingTime(
      cleanSpeakerName(f.speaker) || null, f.timecode, f.text));
  }
  // Участников нет: строка на реплику, время — если оно стоит в начале строки.
  return String(text).split('\n').filter((line) => line.trim())
    .map((line) => withLeadingTime(null, null, line));
}

/** Субтитры собираются, только если начало известно у каждой непустой реплики. */
export function canSubtitle(segments) {
  const spoken = (segments || []).filter((s) => s.text && s.text.trim());
  return spoken.length > 0 && spoken.every((s) => num(s.start) !== null);
}

/** Реплики с отрезком времени для субтитров — правило конца в шапке файла. */
export function subtitleCues(segments) {
  const spoken = (segments || []).filter((s) => s.text && s.text.trim() && num(s.start) !== null);
  return spoken.map((s, index) => {
    const next = spoken[index + 1];
    let end = num(s.end);
    if (end === null || end <= s.start) {
      end = next && next.start > s.start
        ? next.start
        : s.start + Math.max(MIN_CUE_SECONDS, s.text.length / CHARS_PER_SECOND);
    }
    return { speaker: s.speaker, start: s.start, end: round3(end), text: s.text };
  });
}

/** «00:01:05,250» у SRT, «00:01:05.250» у VTT. */
function clock(seconds, separator) {
  const ms = Math.round(seconds * 1000);
  const pad = (value, size = 2) => String(value).padStart(size, '0');
  return `${pad(Math.floor(ms / 3600000))}:${pad(Math.floor(ms / 60000) % 60)}:`
    + `${pad(Math.floor(ms / 1000) % 60)}${separator}${pad(ms % 1000, 3)}`;
}

/**
 * Текст субтитра: пустая строка внутри закончила бы субтитр раньше времени,
 * а «-->» в тексте плеер принимает за строку времени.
 */
function cueText(text) {
  return String(text || '').replace(/\r\n?/g, '\n').split('\n')
    .map((line) => line.trim()).filter(Boolean).join('\n')
    .replace(/-->/g, '→');
}

const oneLine = (value) => String(value || '').replace(/\s+/g, ' ').trim();

export function toSrt(segments) {
  return subtitleCues(segments).map((cue, index) => {
    const speaker = oneLine(cue.speaker);
    return `${index + 1}\n${clock(cue.start, ',')} --> ${clock(cue.end, ',')}\n`
      + `${speaker ? `${speaker}: ` : ''}${cueText(cue.text)}\n`;
  }).join('\n');
}

// В VTT «<», «>» и «&» — разметка, в речи их надо экранировать.
const escapeVtt = (value) => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function toVtt(segments) {
  const cues = subtitleCues(segments).map((cue) => {
    const speaker = oneLine(cue.speaker);
    return `${clock(cue.start, '.')} --> ${clock(cue.end, '.')}\n`
      + `${speaker ? `<v ${escapeVtt(speaker)}>` : ''}${escapeVtt(cueText(cue.text))}\n`;
  });
  return `WEBVTT\n\n${cues.join('\n')}`;
}

/** JSON для интеграций: только известное; языка распознавание не сообщает — поля нет. */
export function toJson(segments, { title = null, durationSec = null } = {}) {
  const list = segments || [];
  const lastTime = list.reduce((max, s) => {
    const t = num(s.end) ?? num(s.start);
    return t !== null && (max === null || t > max) ? t : max;
  }, null);
  const duration = num(durationSec) && durationSec > 0 ? durationSec : lastTime;
  return `${JSON.stringify({
    title: title || null,
    duration_sec: round3(duration),
    segments: list.map((s) => ({
      speaker: s.speaker || null,
      start_sec: round3(num(s.start)),
      end_sec: round3(num(s.end)),
      text: s.text,
    })),
  }, null, 2)}\n`;
}

/** Реплики обратно в текст формата Истории — для markdown из экрана результата. */
function segmentsToText(segments, withTimecodes) {
  return (segments || []).map((s) => {
    let timecode = null;
    if (withTimecodes && num(s.start) !== null) {
      const end = num(s.end) !== null ? ` - ${formatTimecodeHMMSS(s.end)}` : '';
      timecode = `[${formatTimecodeHMMSS(s.start)}${end}]`;
    }
    const head = [s.speaker, timecode].filter(Boolean).join(' ');
    return head ? `${head}: ${s.text}` : s.text;
  }).join('\n');
}

/**
 * Файл выгрузки: { content, ext, mime }.
 *
 * `text` — исходный текст (История): markdown из него тот же, что уходит в
 * DOCX. `segments` — реплики экрана результата. Достаточно одного из двух.
 */
export function buildExport(key, { text = null, segments = null, title = null, durationSec = null,
                                   showTimecodes = true } = {}) {
  const format = EXPORT_FORMATS.find((f) => f.key === key);
  if (!format) throw new Error(`Неизвестный формат выгрузки: ${key}`);
  const list = segments || segmentsFromText(text);

  let content;
  if (key === 'srt') content = toSrt(list);
  else if (key === 'vtt') content = toVtt(list);
  else if (key === 'json') content = toJson(list, { title, durationSec });
  else {
    const source = text !== null && text !== undefined ? text : segmentsToText(list, showTimecodes);
    const body = transcriptToMarkdown(source, { showTimecodes });
    const heading = title ? `# ${escapeMarkdown(title)}\n\n` : '';
    content = `${heading}${body}\n`;
  }
  return { content, ext: format.ext, mime: format.mime };
}
